import { Button } from "@aurumiq/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@aurumiq/ui/components/card";
import { Switch } from "@aurumiq/ui/components/switch";
import { StatusTag } from "@aurumiq/ui/composite/status-tag";
import { relativeTime } from "@aurumiq/ui/lib/format";
import { zodResolver } from "@hookform/resolvers/zod";
import { Copy, Save, Send, SendHorizonal } from "lucide-react";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { PageHeader } from "@/app/page-header";
import { useAuth } from "@/auth/auth-context";
import { SelectField, TextField } from "@/components/field";
import { QueryState, Stat, StatList } from "@/components/query-state";
import {
  useApplyMailSettings,
  useMailSettings,
  useSaveMailSettings,
  useTestMail,
} from "@/features/system/queries";
import type { MailSettings } from "@/lib/types";

const GUID = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
const guidOrBlank = z
  .string()
  .trim()
  .refine((v) => !v || GUID.test(v), "Must be a GUID like 1a2b3c4d-…");

const schema = z
  .object({
    provider: z.enum(["smtp", "graph"]),
    from_name: z.string().trim().min(1, "Required").max(120),
    from_email: z.string().trim().max(320),
    graph_tenant_id: guidOrBlank,
    graph_client_id: guidOrBlank,
    graph_client_secret: z
      .string()
      .trim()
      .max(500)
      .refine((v) => !GUID.test(v), "That is the Secret ID; paste the secret Value"),
    graph_secret_expires_on: z.string(),
    smtp_host: z.string().trim().max(255),
    smtp_port: z.number().int().min(1).max(65535),
    smtp_user: z.string().trim().max(255),
    smtp_password: z.string().max(500),
    smtp_starttls: z.boolean(),
    notifications_enabled: z.boolean(),
    auth_emails_enabled: z.boolean(),
  })
  .superRefine((v, ctx) => {
    if (v.provider === "graph") {
      if (!v.graph_tenant_id)
        ctx.addIssue({ code: "custom", path: ["graph_tenant_id"], message: "Required for Graph" });
      if (!v.graph_client_id)
        ctx.addIssue({ code: "custom", path: ["graph_client_id"], message: "Required for Graph" });
      if (!v.from_email.includes("@"))
        ctx.addIssue({ code: "custom", path: ["from_email"], message: "Sender mailbox required" });
    }
  });
type FormValues = z.infer<typeof schema>;

const toForm = (s: MailSettings): FormValues => ({
  provider: s.provider,
  from_name: s.from_name,
  from_email: s.from_email,
  graph_tenant_id: s.graph_tenant_id,
  graph_client_id: s.graph_client_id,
  graph_client_secret: "",
  graph_secret_expires_on: s.graph_secret_expires_on ?? "",
  smtp_host: s.smtp_host,
  smtp_port: s.smtp_port,
  smtp_user: s.smtp_user,
  smtp_password: "",
  smtp_starttls: s.smtp_starttls,
  notifications_enabled: s.notifications_enabled,
  auth_emails_enabled: s.auth_emails_enabled,
});

export function MailSettingsPage() {
  const q = useMailSettings();
  return (
    <>
      <PageHeader
        title="Email"
        description="How the platform sends e-mail: organisation invites and other notifications, and optionally the auth service's own messages (confirmation, magic link, password reset)."
      />
      <QueryState query={q}>{(s) => <MailForm initial={s} />}</QueryState>
    </>
  );
}

function CopyLine({ value }: { value: string }) {
  return (
    <div className="flex items-center gap-2 font-mono text-xs">
      <span className="truncate">{value}</span>
      <button
        type="button"
        className="text-muted-foreground hover:text-foreground"
        aria-label={`Copy ${value}`}
        onClick={() => navigator.clipboard?.writeText(value)}
      >
        <Copy className="size-3.5" />
      </button>
    </div>
  );
}

function Toggle({
  id,
  label,
  hint,
  checked,
  onChange,
}: {
  id: string;
  label: string;
  hint: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start gap-3 rounded-lg border p-3">
      <Switch id={id} checked={checked} onCheckedChange={onChange} aria-label={label} />
      <div>
        <label htmlFor={id} className="font-medium text-sm">
          {label}
        </label>
        <p className="text-muted-foreground text-xs">{hint}</p>
      </div>
    </div>
  );
}

function MailForm({ initial }: { initial: MailSettings }) {
  const auth = useAuth();
  const save = useSaveMailSettings();
  const apply = useApplyMailSettings();
  const test = useTestMail();
  const [testTo, setTestTo] = useState(auth.user?.email ?? "");
  const form = useForm<FormValues>({ resolver: zodResolver(schema), values: toForm(initial) });
  const provider = form.watch("provider");
  const st = initial.status;
  const onSubmit = form.handleSubmit((v) =>
    save.mutate({
      ...v,
      graph_client_secret: v.graph_client_secret || null,
      graph_secret_expires_on: v.graph_secret_expires_on || null,
      smtp_password: v.smtp_password || null,
    }),
  );
  const dirty = form.formState.isDirty;
  const ready = st.problems.length === 0 && st.effective_transport !== "none";
  const authNeedsApply = initial.auth_emails_enabled !== st.hook_applied || !st.hook_applied;
  const expiry = st.graph_secret_expires_in_days;
  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_380px]">
      <form onSubmit={onSubmit} className="grid gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Transport</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3">
            <div className="grid gap-3 sm:grid-cols-[220px_1fr_1fr]">
              <SelectField
                label="Send with"
                error={form.formState.errors.provider?.message}
                {...form.register("provider")}
              >
                <option value="graph">Microsoft 365 (Graph)</option>
                <option value="smtp">SMTP server</option>
              </SelectField>
              <TextField
                label="From name"
                error={form.formState.errors.from_name?.message}
                {...form.register("from_name")}
              />
              <TextField
                label={provider === "graph" ? "Sender mailbox" : "From address"}
                placeholder="noreply@yourcompany.com"
                hint={
                  provider === "graph"
                    ? "A real mailbox or shared mailbox in the tenant; mail is sent as this user."
                    : "Blank = server default."
                }
                error={form.formState.errors.from_email?.message}
                {...form.register("from_email")}
              />
            </div>

            {provider === "graph" ? (
              <div className="grid gap-3 rounded-lg border bg-muted/40 p-3">
                <div className="grid gap-3 sm:grid-cols-2">
                  <TextField
                    label="Directory (tenant) ID"
                    placeholder="00000000-0000-0000-0000-000000000000"
                    error={form.formState.errors.graph_tenant_id?.message}
                    {...form.register("graph_tenant_id")}
                  />
                  <TextField
                    label="Application (client) ID"
                    placeholder="00000000-0000-0000-0000-000000000000"
                    error={form.formState.errors.graph_client_id?.message}
                    {...form.register("graph_client_id")}
                  />
                </div>
                <div className="grid gap-3 sm:grid-cols-[1fr_180px]">
                  <TextField
                    label="Client secret"
                    type="password"
                    autoComplete="new-password"
                    placeholder={
                      initial.graph_has_secret
                        ? "•••••••• (stored; paste to replace)"
                        : "Secret Value from Certificates & secrets"
                    }
                    hint="Stored encrypted with the server master key. Needs the application permission Mail.Send with admin consent."
                    error={form.formState.errors.graph_client_secret?.message}
                    {...form.register("graph_client_secret")}
                  />
                  <TextField
                    label="Secret expires on"
                    type="date"
                    hint="Optional reminder"
                    error={form.formState.errors.graph_secret_expires_on?.message}
                    {...form.register("graph_secret_expires_on")}
                  />
                </div>
              </div>
            ) : (
              <div className="grid gap-3 rounded-lg border bg-muted/40 p-3 sm:grid-cols-[1fr_110px_1fr_1fr]">
                <TextField
                  label="Host"
                  placeholder="smtp.example.com (blank = server default)"
                  error={form.formState.errors.smtp_host?.message}
                  {...form.register("smtp_host")}
                />
                <TextField
                  label="Port"
                  type="number"
                  error={form.formState.errors.smtp_port?.message}
                  {...form.register("smtp_port", { valueAsNumber: true })}
                />
                <TextField
                  label="Username"
                  autoComplete="off"
                  error={form.formState.errors.smtp_user?.message}
                  {...form.register("smtp_user")}
                />
                <TextField
                  label="Password"
                  type="password"
                  autoComplete="new-password"
                  placeholder={initial.smtp_has_password ? "•••••••• (stored)" : ""}
                  error={form.formState.errors.smtp_password?.message}
                  {...form.register("smtp_password")}
                />
                <Controller
                  control={form.control}
                  name="smtp_starttls"
                  render={({ field }) => (
                    <div className="flex items-center gap-2 text-sm sm:col-span-4">
                      <Switch
                        id="smtp-tls"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        aria-label="Use STARTTLS"
                      />
                      <label htmlFor="smtp-tls">Use STARTTLS</label>
                    </div>
                  )}
                />
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>What is sent</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3">
            <Controller
              control={form.control}
              name="notifications_enabled"
              render={({ field }) => (
                <Toggle
                  id="notif"
                  label="Site notifications"
                  hint="Organisation invites, welcome e-mails after checkout and other notifications sent by the platform."
                  checked={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <Controller
              control={form.control}
              name="auth_emails_enabled"
              render={({ field }) => (
                <Toggle
                  id="authmail"
                  label="Authentication e-mails (magic link, confirmation, password reset, invite)"
                  hint="Routes the auth service's messages through this transport instead of its own SMTP. Needs “Apply to auth service” after saving."
                  checked={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
              <p className="text-muted-foreground text-xs">
                {initial.updated_at
                  ? `Saved ${relativeTime(initial.updated_at)} by ${initial.updated_by ?? "—"}`
                  : "Never saved: using the server defaults."}
              </p>
              <div className="flex gap-2">
                <Button
                  type="submit"
                  variant={dirty ? "default" : "outline"}
                  disabled={save.isPending || !dirty}
                >
                  <Save /> {save.isPending ? "Saving…" : "Save"}
                </Button>
                <Button
                  type="button"
                  variant={authNeedsApply && !dirty ? "default" : "outline"}
                  disabled={dirty || apply.isPending || st.apply_mode === "disabled"}
                  onClick={() => apply.mutate()}
                  title={dirty ? "Save first" : "Point the auth service at this transport"}
                >
                  <Send /> {apply.isPending ? "Applying…" : "Apply to auth service"}
                </Button>
              </div>
            </div>
            {st.problems.length ? (
              <ul className="list-disc rounded-md bg-warning-bg px-3 py-2 pl-7 text-sm text-warning">
                {st.problems.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            ) : null}
          </CardContent>
        </Card>
      </form>

      <div className="grid content-start gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Status</CardTitle>
          </CardHeader>
          <CardContent>
            <StatList>
              <Stat label="Transport in use">
                {st.effective_transport === "graph" ? (
                  <StatusTag variant="ok">MICROSOFT GRAPH</StatusTag>
                ) : st.effective_transport === "smtp" ? (
                  <StatusTag variant="ok">SMTP</StatusTag>
                ) : (
                  <StatusTag variant="err">NONE</StatusTag>
                )}
              </Stat>
              <Stat label="Auth e-mails via API">
                {st.hook_applied && initial.auth_emails_enabled ? (
                  <StatusTag variant="ok">ON</StatusTag>
                ) : initial.auth_emails_enabled ? (
                  <StatusTag variant="warn">NOT APPLIED</StatusTag>
                ) : (
                  <StatusTag>OFF</StatusTag>
                )}
              </Stat>
              <Stat label="Last test">
                {st.last_test_result
                  ? `${st.last_test_result.slice(0, 80)}${st.last_test_at ? ` · ${relativeTime(st.last_test_at)}` : ""}`
                  : "—"}
              </Stat>
              <Stat label="Graph secret expiry">
                {expiry === null ? (
                  "—"
                ) : expiry < 0 ? (
                  <StatusTag variant="err">EXPIRED</StatusTag>
                ) : expiry < 30 ? (
                  <StatusTag variant="warn">{expiry} DAYS</StatusTag>
                ) : (
                  `${expiry} days`
                )}
              </Stat>
            </StatList>
            <div className="mt-4 flex gap-2">
              <input
                className="h-9 min-w-0 flex-1 rounded-lg border border-input bg-card px-2.5 text-sm"
                type="email"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                placeholder="you@company.com"
                aria-label="Send a test e-mail to"
              />
              <Button
                type="button"
                variant="outline"
                disabled={!ready || dirty || !testTo || test.isPending}
                onClick={() => test.mutate(testTo)}
                title={
                  dirty ? "Save first" : ready ? "Send a test e-mail" : "Fix the problems first"
                }
              >
                <SendHorizonal /> {test.isPending ? "Sending…" : "Send test"}
              </Button>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Microsoft 365 set-up (one time)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <ol className="list-decimal space-y-2 pl-4">
              <li>
                Entra admin centre → <i>App registrations</i> → <b>New registration</b> (e.g.
                “AurumIQ Mail”). No redirect URI is needed. You may reuse the single sign-on app.
              </li>
              <li>
                <i>API permissions</i> → Microsoft Graph → <b>Application</b> permissions →{" "}
                <code>Mail.Send</code> → <b>Grant admin consent</b>.
              </li>
              <li>
                <i>Certificates &amp; secrets</i> → new client secret → copy the <b>Value</b>.
              </li>
              <li>
                Create or pick the sender mailbox (a shared mailbox works and needs no licence for
                sending). Recommended: restrict the app to that mailbox with an Exchange{" "}
                <i>application access policy</i>.
              </li>
              <li>
                Fill the form, <b>Save</b>, then <b>Send test</b>. Turn on authentication e-mails
                and <b>Apply to auth service</b> when the test arrives.
              </li>
            </ol>
            <div>
              <div className="mb-1 font-semibold text-xs uppercase tracking-wide">
                Auth hook endpoint (internal)
              </div>
              <CopyLine value={st.hook_uri} />
            </div>
            <p className="text-muted-foreground text-xs">
              Applying restarts the auth service; customer sign-ins pause for a few seconds.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
